import type {
  LookupAdminRow,
  LookupCreateInput,
  LookupCreateResult,
  LookupMergeResult,
  LookupSeedResult,
  LookupUpdateInput,
  PaginationMeta,
  WineLookupKind,
} from '@gwc/shared';
import { api } from '../lib/api';

export interface LookupManageParams {
  q?: string;
  /** true = only entries awaiting review; omitted = everything. */
  needsReview?: boolean;
  page?: number;
  pageSize?: number;
}

/** The admin side of the wine lookup lists (Setup page). */
export const lookupAdminApi = {
  list: (kind: WineLookupKind, params: LookupManageParams = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ).toString();
    return api.get<{ data: LookupAdminRow[]; meta: PaginationMeta }>(
      `/lookups/${kind}/manage${qs ? `?${qs}` : ''}`,
    );
  },
  /** An admin's addition starts proofread. 200 + `created: false` when it already exists. */
  add: (kind: WineLookupKind, body: LookupCreateInput) =>
    api.post<LookupCreateResult>(`/lookups/${kind}`, body),
  /** `catalogConflicts`: Cellar wines a rename made identical to another (flagged for review). */
  update: (kind: WineLookupKind, id: string, body: LookupUpdateInput) =>
    api.patch<{ data: LookupAdminRow; catalogConflicts?: string[] }>(`/lookups/${kind}/${id}`, body),
  merge: (kind: WineLookupKind, id: string, intoId: string) =>
    api.post<{ data: LookupMergeResult }>(`/lookups/${kind}/${id}/merge`, { intoId }).then((r) => r.data),
  review: (kind: WineLookupKind, ids: string[]) =>
    api.post<{ data: { updated: number } }>(`/lookups/${kind}/review`, { ids }).then((r) => r.data),
  remove: (kind: WineLookupKind, id: string) =>
    api.del<{ data: { ok: true } }>(`/lookups/${kind}/${id}`).then((r) => r.data),
  seed: () => api.post<{ data: LookupSeedResult }>('/lookups/seed', {}).then((r) => r.data),
};
